import * as STD from '@emersya/mpa-std-lib';
import CM       from '@emersya/mpa-components-manager-lib';

export interface IReplicatorComponentConfiguration extends CM.DOMComponent.IConfiguration {
	readonly targetOrPath : CM.Path.TPathRaw | CM.DOMComponent<any, any, any> | null;
};

export class ReplicatorComponent
	extends CM.DOMComponent<HTMLDivElement, ReplicatorComponent, IReplicatorComponentConfiguration> {
	component_DOM : HTMLDivElement;
	target : CM.DOMComponent<HTMLElement, any, any> | null = null;
	currentClonedTarget : HTMLElement | null = null;
	updateDOMFromTarget : typeof this.__updateDOMFromTarget;
	private registeredListenersOnTarget : Record<string, STD.events.Listener<CM.Component<any, any>, any>> = {};
	private isCurrentlyDispatchingEvent = 0;
	private awaitedNextEvent : 'pointerup' | 'click' | null = null;
	scrollTop = 0;

	async updateTarget(
		targetOrPath : CM.Path.TPathRaw | CM.DOMComponent<any, any, any> | null,
		reapplyIfSame = false
	)
	{
		const oldTarget = this.target;
		if (targetOrPath) {
			let target : CM.DOMComponent<any, any, any>;
			if (typeof targetOrPath === 'string') {
				const targetRealPath = CM.Path.parse(targetOrPath);
				const tmp = this.component_find(targetRealPath);
				if (!tmp) {
					this.component_throwUserError('');
				}
				target = <CM.DOMComponent<any, any, any>> tmp;
			} else {
				target = targetOrPath;
			}
			if (!(target instanceof CM.DOMComponent)) {
				this.component_throwUserError('Target is not a DOMComponent', {
					target,
					targetOrPath
				});
			}
			if (target === this.target) {
				if (!reapplyIfSame) {
					// return;
				}
				await this.updateTarget(null);
			}
			this.target = target;
			this.registeredListenersOnTarget.onValue = this.target.addEventListener(
				'value',
				this.onReplicatedUpdated.bind(this),
				{
					listener :   this,
					subType :    '*',
					isAtTarget : false,
					priority :   5000
				}
			);
		} else {
			if (this.target) {
				if (!this.registeredListenersOnTarget.onValue) {
					this.component_throwLibraryError('Has target but no onValue registered listener');
				}
				this.target.removeEventListener(this.registeredListenersOnTarget.onValue);
			}
			this.target = null;
		}
		if (this.target) {
			const replicationEvent = new STD.events.Event<
				CM.Component<any, any>,
				CM.Component<any, any>
			>({
				type :              'replication',
				bubblingType :      'childs',
				bubblingDirection : 'down',
				cancelable :        false,
				subType :           'enable',
				target :            this.target,
				details :           {
					replicator : this
				}
			});
			await this.target.dispatchEvent(replicationEvent);
			this.target.component_virtualVisibilityEnable();
			await this.target.component_dispatchVisibilityUpdate(true, true, true);
		} else {
			if (oldTarget) {
				const replicationEvent = new STD.events.Event<
					CM.Component<any, any>,
					CM.Component<any, any>
				>({
					type :              'replication',
					bubblingType :      'childs',
					bubblingDirection : 'down',
					cancelable :        false,
					subType :           'disable',
					target :            oldTarget,
					details :           {
						replicator : this
					}
				});
				await oldTarget.dispatchEvent(replicationEvent);
			}
		}
		if (this.target) {
			const inputs = <any[]> this.target.getAllChilds().filter(x => x.static.type === 'input-number');
			for (const x of inputs) {
				await x.recomputeBounds();
			}
		}
		this.updateDOMFromTarget();
	};

	private __updateDOMFromTarget() {
		if (this.isCurrentlyDispatchingEvent !== 0) {
			return;
		}
		while (this.component_DOM.children.length > 0) {
			(<any> this.component_DOM.children[ 0 ]).remove();
		}
		const t = this.target
		if (t) {
			t.component_DOM.offsetHeight;
			const clonedDOM = <Element> (<Element> t.component_DOM).cloneNode(true);
			for (const replicatedComponentDOM of Array.from(clonedDOM.querySelectorAll('[data-component-type]'))) {
				replicatedComponentDOM.classList.add('fakeComponent');
			}
			clonedDOM.classList.add('fakeComponent');
			this.component_DOM.appendChild(clonedDOM);
		}
		this.component_DOM.scrollTop = this.scrollTop;
	};

	async notifyUpdate() {
		// await this.updateTarget(this.target, true);
	};

	override async component_start(event : STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_start(event);
		if (this.component_configuration.targetOrPath) {
			await this.updateTarget(this.component_configuration.targetOrPath, true);
		}
	};

	private onEvents_before() {
		if (this.isCurrentlyDispatchingEvent === 0) {
			this.component_DOM.style.pointerEvents = 'none';
		}
		this.isCurrentlyDispatchingEvent++;
	};

	private onEvents_after(handledEventsCount : number) {
		this.isCurrentlyDispatchingEvent -= handledEventsCount;
		if (this.isCurrentlyDispatchingEvent === 0) {
			this.component_DOM.style.pointerEvents = '';
		}
	};

	private redispatchEvent = async (event : Event, target : unknown) => {
		if (!target || !(target instanceof HTMLElement)) {
			this.component_throwLibraryError('Event with incompatible target received!', {
				event
			});
		}
		if (event.type === 'pointerdown') {
			this.awaitedNextEvent = 'pointerup';
		} else if (event.type === 'pointerup') {
			this.awaitedNextEvent = 'click';
		} else if (event.type === 'click') {
			this.awaitedNextEvent = null;
		}
		(<any> event).details = {
			fromReplicat : this
		};
		const targetDOM = this.getOriginalNodeFromReplicatedNode(target);
		targetDOM.dispatchEvent(event); // /!\Do not await the dispatch because it will prevent the rendering if listener is async.
	};

	private async onEvents(events : Event[]) {
		try {
			if (this.target) {
				await this.target.component_dispatchVisibilityUpdate(true, true, true);
			}
			let previousEvent : Event | null = null;
			for (const event of events) {
				await this.redispatchEvent(STD.events.cloneNative(event), event.target);
				previousEvent = event;
			}

			while (this.awaitedNextEvent) {
				if (!previousEvent) {
					throw new Error;
				}
				if ((this.awaitedNextEvent === 'pointerup') && STD.DOM.mouse.hasButtonPressed) {
					// We must dispatch pointerup event only if we have missed its interception, which can be detected if no mouse button is pressed.
					// We can free awaited next event because it will be triggered when mouse button will be stop being pressed.
					this.awaitedNextEvent = null;
					break;
				}
				await this.redispatchEvent(new MouseEvent(this.awaitedNextEvent, previousEvent), previousEvent.target);
			}

			if (this.target) {
				const inputs = <any[]> this.target.getAllChilds().filter(x => x.static.type === 'input-number');
				for (const x of inputs) {
					await x.recomputeBounds();
				}
			}
			this.onEvents_after(events.length);
		} catch (error : any) {
			this.onEvents_after(events.length);
			throw error;
		}
	};

	getOriginalNodeFromReplicatedNode(node : HTMLElement) {
		if (!this.target) {
			this.component_throwLibraryError('Event received but there are no target!', {
				node
			});
		}
		const pathCSS = this.component_computeCSSSelector(node);
		const originalNode = (
			pathCSS.includes(' > ') ? this.target.component_querySelector(pathCSS) :
				this.target.component_DOM
		);
		if (!originalNode) {
			this.component_throwLibraryError('Original node not found!', {
				node,
				pathCSS
			});
		}
		return (originalNode);
	};

	getOriginalComponentFromReplicatedNode(node : HTMLElement) {
		let originalNode : Element | null = this.getOriginalNodeFromReplicatedNode(node);
		let component : CM.DOMComponent<any, any, any> | null = null;
		while (originalNode) {
			if (originalNode.details && originalNode.details.component) {
				component = originalNode.details.component;
				break;
			}
			originalNode = originalNode.parentElement;
		}
		if (!component) {
			this.component_throwLibraryError('No component found from node', {
				node,
				originalNode
			});
		}
		return (component);
	};

	getReplicatedNodeFromOriginalComponent(component : CM.Component<any, any>) {
		if (!this.target) {
			this.component_throwUserError('');
		}
		const prefix = this.target?.component_parent?.component_getPath() || null;
		let pathSuffix = component.component_getPath();
		if (prefix) {
			if (!pathSuffix.startsWith(prefix)) {
				this.component_throwUserError('??', {
					prefix,
					pathSuffix
				});
			}
			pathSuffix = pathSuffix.replace(prefix, '');
		}
		const selectorCSS = pathSuffix.split(/\.|:/gm).filter(x => x).map(x => `[data-component-id="${ x }"]`).join(' ');
		const node = this.component_DOM.querySelector(selectorCSS);
		return (node);
	};

	private onReplicatEvents(events : [ Event ][]) {
		requestIdleCallback(this.onEvents.bind(this, events.flat()));
	};

	private onReplicatedUpdated(event : STD.events.Event<any, any>) {
		void event;
		this.updateDOMFromTarget();
	};

	override async component_onActivationChange(event : STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_onActivationChange(event);
		if (event.subType === 'enabled') {
			this.scrollTop = 0;
			await this.updateTarget(this.target, true);
		} else {
			await this.updateTarget(null);
		}
	};

	onReplicatScroll() {
		this.scrollTop = this.component_DOM.scrollTop;
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IReplicatorComponentConfiguration
	)
	{
		super(descriptor, configuration);
		this.updateDOMFromTarget = STD.misc.createDebouncer({
			fn :    this.__updateDOMFromTarget.bind(this),
			delay : 10
		});
		const f = STD.misc.createDebouncerAccumulator({
			fn :      this.onReplicatEvents.bind(this),
			delay :   10,
			forEach : () => {
				this.onEvents_before();
			}
		});
		this.component_DOM = <HTMLDivElement> STD.DOM.factory({
			events : {
				click :       f,
				pointerdown : f,
				pointerup :   f,
				scroll :      STD.misc.createDebouncer({
					fn :    this.onReplicatScroll.bind(this),
					delay : 5
				})
			}
		});
	};
};
export namespace NS {
	export type Component = ReplicatorComponent;
	export const Component = ReplicatorComponent;
	export type Configuration = IReplicatorComponentConfiguration;
}

CM.declare({
	id :   'replicator',
	type : ReplicatorComponent
});
